import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { Cookies } from 'react-cookie';
import { useHistory } from 'react-router-dom';

import ICartProduct from '~/interfaces/ICartProduct';
import { useAnalytics } from './analytics';
import IAddress from '~/interfaces/IAddress';
import IDistance from '~/interfaces/IDistance';
import ICoupon from '~/interfaces/ICoupon';

interface CartContextData {
  address: IAddress;
  products: ICartProduct[];
  deliveryMethod: string;
  distances: IDistance[];
  updateDistances(data: IDistance[]): void;
  updateAddress(data: IAddress): void;
  getAddress(lat: string, lon: string): Promise<IAddress>;
  changeDeliveryMethod(method: string): void;
  add(product: ICartProduct): Promise<void>;
  plus(index: number): void;
  minus(index: number): void;
  clearCart(): void;
  addressFilled: boolean;
  setAddressFilled: (value: boolean) => void;
  coupon: ICoupon;
  setCoupon: (value: ICoupon) => void;
  fidelitDiscount: number;
  setFidelitDiscount: (value: number) => void;
}

const cookies = new Cookies();
const { REACT_APP_APP_NAME } = process.env;

const ProductsContext = createContext<CartContextData>({} as CartContextData);

export const CartProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const { logAddToCart } = useAnalytics();

  const [deliveryMethod, setDeliveryMethod] = useState('1');
  const [distances, setDistances] = useState<IDistance[]>([]);
  const [addressFilled, setAddressFilled] = useState(false);
  const [coupon, setCoupon] = useState({} as ICoupon);
  const [fidelitDiscount, setFidelitDiscount] = useState(0);

  const appDomain = useMemo(() => {
    const paths = history.location.pathname.split('/');
    const [, id] = paths;

    if (id) return id;

    return '';
  }, [history]);

  const [address, setAddress] = useState(() => {
    const tmp = localStorage.getItem(
      `@${REACT_APP_APP_NAME}:${appDomain}:address`,
    );
    //  cookies.get(`@${REACT_APP_APP_NAME}:${appDomain}:address`);

    if (tmp) return JSON.parse(tmp);

    return {} as IAddress;
  });

  useEffect(() => {
    const tmp = localStorage.getItem(
      `@${REACT_APP_APP_NAME}:${appDomain}:address`,
    );
    if (tmp) setAddress(JSON.parse(tmp));
  }, [appDomain]);

  const [products, setProducts] = useState<ICartProduct[]>(() => {
    const cart = cookies.get(`@${REACT_APP_APP_NAME}:${appDomain}:cart`);

    if (cart) return cart;

    return [];
  });

  const updateDistances = useCallback((data: IDistance[]) => {
    setDistances(data);
  }, []);

  const plus = useCallback(
    (index: number) => {
      const items = [...products];

      items[index].quantity += items[index].measure === 'KG' ? 0.1 : 1;

      setProducts(items);

      cookies.set(
        `@${REACT_APP_APP_NAME}:${appDomain}:cart`,
        JSON.stringify(items),
        {
          path: '/',
        },
      );
    },
    [products, appDomain],
  );

  const minus = useCallback(
    (index: number) => {
      const items = [...products];

      items[index].quantity -= items[index].measure === 'KG' ? 0.1 : 1;

      if (items[index].quantity === 0 || items[index].quantity < 0.1) {
        items.splice(index, 1);
      }

      setProducts(items);

      cookies.set(
        `@${REACT_APP_APP_NAME}:${appDomain}:cart`,
        JSON.stringify(items),
        {
          path: '/',
        },
      );
    },
    [products, appDomain],
  );

  const add = useCallback(
    async (product: ICartProduct) => {
      // const findIndex = products.findIndex(item => item.uid === product.uid);
      let items = [];

      // if (findIndex !== -1) {
      //   items = [...products];

      //   items[findIndex].quantity += product.quantity;

      //   setProducts(items);
      // } else {
      //   items = [...products, product];
      // }

      items = [...products, product];

      setProducts(items);

      // Rastrear evento de add_to_cart no Analytics
      logAddToCart(product.name, product.uid, parseFloat(product.value));

      cookies.set(
        `@${REACT_APP_APP_NAME}:${appDomain}:cart`,
        JSON.stringify(items),
        {
          path: '/',
        },
      );
    },
    [products, appDomain, logAddToCart],
  );

  const changeDeliveryMethod = useCallback((method: string) => {
    setDeliveryMethod(method);
  }, []);

  const getAddress = useCallback(
    async (lat: string, lon: string) => {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
      );

      const data = await response.text();

      const parsseData = JSON.parse(data) as any;

      parsseData.address.neighbourhood = parsseData.address.suburb;

      setAddress({
        ...address,
        ...parsseData.address,
      });

      return parsseData.address;
    },
    [address],
  );

  const updateAddress = useCallback(
    (data: IAddress) => {
      setAddress(data);
      localStorage.setItem(
        `@${REACT_APP_APP_NAME}:${appDomain}:address`,
        JSON.stringify(data),
      );

      // cookies.set(
      //   `@${REACT_APP_APP_NAME}:${appDomain}:address`,
      //   JSON.stringify(data),
      //   {
      //     path: '/',
      //   },
      // );
    },
    [appDomain],
  );

  const clearCart = useCallback(() => {
    cookies.remove(`@${REACT_APP_APP_NAME}:${appDomain}:cart`);
    setProducts([]);
  }, [appDomain]);

  return (
    <ProductsContext.Provider
      value={{
        clearCart,
        address,
        products,
        add,
        minus,
        plus,
        changeDeliveryMethod,
        deliveryMethod,
        getAddress,
        updateAddress,
        distances,
        updateDistances,
        addressFilled,
        setAddressFilled,
        coupon,
        setCoupon,
        fidelitDiscount,
        setFidelitDiscount,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export function useCart(): CartContextData {
  const context = useContext(ProductsContext);
  return context;
}
